import { FiBarChart2 } from "react-icons/fi";
import { cn } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import { useAverageScores } from "../../../../hooks/usePanelScoringQueries";
import { calcWeightedScore, calcWeightedDiff, formatWeightedValue } from "../../../../utils/scoring";
import { DiffBadge } from "../../../DiffBadge";
import type { ScoringMode } from "../../../../hooks/usePanelScoringQueries";

interface AverageScoringTableProps {
	allSubmitted: boolean;
	evalId: number;
	mode?: ScoringMode;
	rfxId: number;
}

export const AverageScoringTable: React.FC<AverageScoringTableProps> = ({
	rfxId,
	evalId,
	allSubmitted,
	mode = "initial",
}) => {
	const isFinal = mode === "final";

	const { data, isLoading, error } = useAverageScores(
		allSubmitted ? rfxId : undefined,
		allSubmitted ? evalId : undefined,
		mode
	);

	const { data: initialData } = useAverageScores(
		isFinal && allSubmitted ? rfxId : undefined,
		isFinal && allSubmitted ? evalId : undefined,
		"initial"
	);

	if (!allSubmitted) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-6 text-center"
				data-testid="average-scoring-locked"
			>
				<FiBarChart2 size={24} className="mx-auto mb-2 text-gray-300" aria-hidden="true" />
				<p className="text-sm text-gray-400">
					Average scores will appear once all panel members have submitted.
				</p>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="space-y-2" data-testid="average-scoring-loading">
				{[1, 2].map((i) => (
					<SkeletonBase key={i} startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				))}
			</div>
		);
	}

	if (error || !data) {
		return (
			<p className="text-sm text-red-600" data-testid="average-scoring-error">
				Failed to load average scores.
			</p>
		);
	}

	const { suppliers, scoring_method, total_members } = data;

	const initialSupplierMap = new Map(
		(initialData?.suppliers ?? []).map((s) => [s.invite_id, s])
	);

	if (suppliers.length === 0) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-400 text-sm"
				data-testid="average-scoring-empty"
			>
				No scoring data available.
			</div>
		);
	}

	return (
		<div data-testid="average-scoring-table">
			<div className="mb-3 flex items-center justify-between">
				<h4 className="text-sm font-medium text-gray-700">
					Average Scores
				</h4>
				<span className="text-xs text-gray-500">
					{scoring_method} · {total_members} member{total_members !== 1 ? "s" : ""}
				</span>
			</div>
			<div className="space-y-4">
				{suppliers.map((supplier) => {
					const initialSupplier = initialSupplierMap.get(supplier.invite_id);
					const initialCriteriaMap = new Map(
						(initialSupplier?.criteria ?? []).map((c) => [c.rfx_criteria_id, c])
					);

					return (
						<div
							key={supplier.invite_id}
							className="rounded-lg border border-gray-200 bg-white"
							data-testid={`average-supplier-${supplier.invite_id}`}
						>
							<div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
								<span className="text-sm font-medium text-gray-900">
									{supplier.supplier_name}
								</span>
								<div className="flex items-center gap-2">
									{isFinal && initialSupplier?.weighted_total !== null && initialSupplier?.weighted_total !== undefined && (
										<span
											className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-gray-100 text-gray-500"
										>
											<FiBarChart2 size={10} aria-hidden="true" />
											{initialSupplier.weighted_total.toFixed(1)}
										</span>
									)}
									{supplier.weighted_total !== null && (
										<span
											className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-[#e8eeff] text-[#0031DD]"
										>
											<FiBarChart2 size={10} aria-hidden="true" />
											{supplier.weighted_total.toFixed(1)}
										</span>
									)}
								</div>
							</div>
							<div className="overflow-x-auto">
								<table className="w-full border-collapse text-sm">
									<thead>
										<tr className="text-left text-xs text-gray-400">
											<th className="px-4 py-2 font-medium">Criterion</th>
											<th className="px-4 py-2 font-medium text-right w-16">Weight</th>
											{isFinal && <th className="px-4 py-2 font-medium text-right w-20">Initial</th>}
											{isFinal && <th className="px-4 py-2 font-medium text-right w-20">Weighted</th>}
											<th className="px-4 py-2 font-medium text-right w-20">{isFinal ? "Final" : "Avg Score"}</th>
											<th className="px-4 py-2 font-medium text-right w-20">Weighted</th>
											{isFinal && <th className="px-4 py-2 font-medium text-right w-14">+/−</th>}
											<th className="px-4 py-2 font-medium text-right w-20">Responses</th>
										</tr>
									</thead>
									<tbody>
										{supplier.criteria.map((c) => {
											const initialCriterion = initialCriteriaMap.get(c.rfx_criteria_id);
											const initialAvg = initialCriterion?.average_score ?? null;
											const weighted = calcWeightedScore(c.average_score, c.criterion_weight);
											const initialWeighted = calcWeightedScore(initialAvg, c.criterion_weight);
											const diff = isFinal ? calcWeightedDiff(c.average_score, initialAvg, c.criterion_weight) : null;

											return (
												<tr
													key={c.rfx_criteria_id}
													className="border-t border-gray-50 hover:bg-blue-50"
												>
													<td className="px-4 py-2 text-gray-700">
														{c.criterion_name}
													</td>
													<td className="px-4 py-2 text-right text-gray-500 tabular-nums">
														{c.criterion_weight}%
													</td>
													{isFinal && (
														<td className="px-4 py-2 text-right text-gray-400 tabular-nums">
															{initialAvg !== null ? initialAvg.toFixed(1) : "—"}
														</td>
													)}
													{isFinal && (
														<td className="px-4 py-2 text-right text-gray-400 tabular-nums">
															{formatWeightedValue(initialWeighted)}
														</td>
													)}
													<td className={cn(
														"px-4 py-2 text-right tabular-nums",
														isFinal ? "font-semibold text-gray-900" : "font-medium"
													)}>
														{c.average_score !== null
															? c.average_score.toFixed(1)
															: "—"}
													</td>
													<td className={cn(
														"px-4 py-2 text-right tabular-nums",
														isFinal ? "font-semibold text-gray-900" : "font-medium text-gray-500"
													)}>
														{formatWeightedValue(weighted)}
													</td>
													{isFinal && (
														<td className="px-4 py-2 text-right tabular-nums text-xs font-semibold">
															<DiffBadge diff={diff} placeholder="—" />
														</td>
													)}
													<td className="px-4 py-2 text-right text-gray-500 tabular-nums">
														{c.score_count}
													</td>
												</tr>
											);
										})}
									</tbody>
									<tfoot>
									{(() => {
										const initialTotal = supplier.criteria.reduce((sum, c) => {
											const ic = initialCriteriaMap.get(c.rfx_criteria_id);
											const w = calcWeightedScore(ic?.average_score ?? null, c.criterion_weight);
											return w !== null ? sum + w : sum;
										}, 0);
										const hasInitial = supplier.criteria.some((c) => {
											const ic = initialCriteriaMap.get(c.rfx_criteria_id);
											return ic?.average_score != null;
										});
										const finalTotal = supplier.criteria.reduce((sum, c) => {
											const w = calcWeightedScore(c.average_score, c.criterion_weight);
											return w !== null ? sum + w : sum;
										}, 0);
										const hasFinal = supplier.criteria.some((c) => c.average_score !== null);
										const totalDiff = hasInitial && hasFinal ? finalTotal - initialTotal : null;

										return (
											<tr className="border-t border-gray-200">
												<td className="px-4 py-2 text-sm font-medium text-gray-900">Total</td>
												<td className="px-4 py-2" />
												{isFinal && <td className="px-4 py-2" />}
												{isFinal && (
													<td className="px-4 py-2 text-right text-gray-400 tabular-nums">
														{hasInitial ? formatWeightedValue(initialTotal) : "—"}
													</td>
												)}
												<td className="px-4 py-2" />
												<td className="px-4 py-2 text-right font-bold tabular-nums text-gray-900">
													{hasFinal ? formatWeightedValue(finalTotal) : "—"}
												</td>
												{isFinal && (
													<td className="px-4 py-2 text-right tabular-nums text-xs font-semibold">
														<DiffBadge diff={totalDiff} placeholder="—" />
													</td>
												)}
												<td className="px-4 py-2" />
											</tr>
										);
									})()}
									</tfoot>
								</table>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};
